import { useCallback, useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from "react";
import Cookies from "js-cookie";
import moment from "moment";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";

import { API_ANNOUNCEMENT, API_ANNOUNCEMENT_ATTACHMENT, API_ANNOUNCEMENT_UPDATE } from "../../../config/Endpoints.js";
import { getAnnouncementEventTypeOptions } from "../../../constants/announcementEventTypeOptions.js";
import { deleteApi, getApi, putApi } from "../../../utils/api.js";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import { Badge, Button, FileField, Modal, Select, TextArea, TextField } from "../../components/ui/index.ts";
import { ResponsiveDataTable, type ResponsiveColumn } from "../../components/ui/responsive-table/responsive-table.tsx";
import { ensureV2DatepickerPortal, V2_DATEPICKER_PORTAL_ID } from "../../components/ui/date-picker/date-picker.tsx";
import { useSqeLabs } from "../../hooks/use-sqe-labs.ts";
import { showError, showSuccess } from "../../utils/toast.ts";
import { announcementStatusLabel, announcementStatusVariant, type AnnouncementRow } from "./announcement-types.ts";
import styles from "./announcement-management.module.css";

type PageProps = { title?: string; description?: string };
type AnnouncementForm = {
  title: string;
  description: string;
  eventType: string;
  presentedBy: string;
  startDateTime: Date | null;
  endDateTime: Date | null;
  venue: string;
  mode: string;
  status: string;
  file: File | string | null;
};

const EMPTY_FORM: AnnouncementForm = {
  title: "", description: "", eventType: "", presentedBy: "",
  startDateTime: null, endDateTime: null, venue: "", mode: "", status: "", file: null,
};

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

const ANNOUNCEMENT_SCHEMA = Yup.object().shape({
  eventType: Yup.string().trim().required("Event Type is required"),
  title: Yup.string().trim().required("Title is required"),
  description: Yup.string().trim().required("Description is required"),
  startDateTime: Yup.date().required("Start Date Time is required"),
  endDateTime: Yup.date().required("End Date Time is required").test(
    "after-start",
    "'End Date Time' must be later than or equal to 'start Date Time'",
    function (endDateTime) {
      const startDateTime = this.parent.startDateTime as Date | null;
      return !startDateTime || !endDateTime || endDateTime.getTime() >= startDateTime.getTime();
    },
  ),
  presentedBy: Yup.string().when("eventType", {
    is: (eventType: string) => eventType !== "Display Announcement",
    then: (schema) => schema.trim().required("Presented By is required"),
  }),
  venue: Yup.string().when("eventType", {
    is: (eventType: string) => eventType !== "Display Announcement",
    then: (schema) => schema.trim().required("Venue is required"),
  }),
  mode: Yup.string().when("eventType", {
    is: (eventType: string) => eventType !== "Display Announcement",
    then: (schema) => schema.trim().required("Mode is required"),
  }),
});

function preventLeadingSpace(event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
  if (event.key === " " && event.currentTarget.selectionStart === 0) event.preventDefault();
}

function pdfFileName(path: string | null | undefined): string | null {
  if (!path) return null;
  return path.split("/").reverse().find((part) => part.endsWith(".pdf")) ?? null;
}

function apiMessage(response: any): string {
  return response?.data?.data?.message ?? response?.data?.message ?? "Announcement updated successfully.";
}

export default function CreateAnnouncementPage({ title, description }: PageProps) {
  const isSqeLabs = useSqeLabs();
  const accessToken = Cookies.get("accessToken");
  const eventTypeOptions = useMemo(() => getAnnouncementEventTypeOptions(isSqeLabs), [isSqeLabs]);
  const [selectedStatus, setSelectedStatus] = useState("PENDING");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRequest, setTotalRequest] = useState(0);
  const [rows, setRows] = useState<AnnouncementRow[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showDeleteAttachment, setShowDeleteAttachment] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<AnnouncementRow | null>(null);
  const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<number | null>(null);
  const [isFileChange, setIsFileChange] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [formData, setFormData] = useState<AnnouncementForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => { ensureV2DatepickerPortal(); }, []);

  const fetchList = useCallback(async () => {
    setIsLoading(true);
    try {
      const status = selectedStatus || "PENDING";
      const res = await getApi(`${API_ANNOUNCEMENT}?pageSize=${pageSize}&page=${currentPage}&status=${status}`, accessToken);
      if (res.status === 200) {
        const body = res?.data?.data;
        setRows(Array.isArray(body?.data) ? body.data : []);
        setTotalRequest(Number(body?.totalPolicy) || 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, currentPage, pageSize, selectedStatus]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  const filteredRows = useMemo(() => rows.filter((row) => row.title.toLowerCase().includes(searchTerm.toLowerCase())), [rows, searchTerm]);
  const filterPassedTime = (time: Date) => new Date().getTime() < new Date(time).getTime();

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setErrors({});
    setIsFileChange(false);
    setSelectedAnnouncement(null);
    setFileInputKey((key) => key + 1);
  };

  const openCreate = () => { resetForm(); setShowEditor(true); };
  const openEdit = (row: AnnouncementRow) => {
    setSelectedAnnouncement(row);
    setFormData({
      title: row.title ?? "", description: row.description ?? "", eventType: row.eventType ?? "",
      presentedBy: row.presentedBy ?? "", startDateTime: row.startDateTime ? new Date(row.startDateTime) : null,
      endDateTime: row.endDateTime ? new Date(row.endDateTime) : null, venue: row.venue ?? "",
      mode: row.mode ?? "", status: selectedStatus, file: row.file ?? null,
    });
    setIsFileChange(Boolean(row.id));
    setErrors({});
    setShowEditor(true);
  };

  const updateField = <K extends keyof AnnouncementForm>(field: K, value: AnnouncementForm[K]) => {
    setFormData((current) => ({ ...current, [field]: value }));
    if (errors[field]) setErrors((current) => ({ ...current, [field]: "" }));
  };

  const handleFileChange = (file: File | null) => {
    if (!file) { updateField("file", null); return; }
    if (file.size > 8 * 1024 * 1024) {
      showError("File size exceeds the maximum limit of 8MB.");
      setFileInputKey((key) => key + 1);
      return;
    }
    if (!["application/pdf", "image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      showError("Unsupported file type. Please upload a valid PDF, JPG, PNG, or JPEG.");
      setFileInputKey((key) => key + 1);
      return;
    }
    updateField("file", file);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsButtonLoading(true);
    setErrors({});
    try {
      await ANNOUNCEMENT_SCHEMA.validate(formData, { abortEarly: false });
      const payload = new FormData();
      payload.append("title", formData.title);
      payload.append("description", formData.description);
      payload.append("eventType", formData.eventType);
      payload.append("presentedBy", formData.presentedBy);
      payload.append("startDateTime", moment(formData.startDateTime).format("YYYY-MM-DDTHH:mm:ss"));
      payload.append("endDateTime", moment(formData.endDateTime).format("YYYY-MM-DDTHH:mm:ss"));
      payload.append("venue", formData.venue);
      payload.append("mode", formData.mode);
      if (formData.file) payload.append("file", formData.file);
      if (formData.status || selectedAnnouncement?.id) {
        payload.append("status", formData.status.toUpperCase());
        payload.append("id", String(selectedAnnouncement?.id ?? ""));
      }
      const response = selectedAnnouncement?.id
        ? await putApi(API_ANNOUNCEMENT_UPDATE, payload, accessToken)
        : await putApi(API_ANNOUNCEMENT, payload, accessToken);
      showSuccess(apiMessage(response));
      setShowEditor(false);
      resetForm();
      void fetchList();
    } catch (error: any) {
      if (Array.isArray(error?.inner)) {
        const nextErrors: Record<string, string> = {};
        error.inner.forEach((issue: { path?: string; message: string }) => {
          if (issue.path) nextErrors[issue.path] = issue.message;
        });
        setErrors(nextErrors);
      } else {
        showError(error?.response?.data?.message ?? "Something went wrong. Please try again.");
      }
    } finally {
      setIsButtonLoading(false);
    }
  };

  const handleDelete = async () => {
    if (selectedDeleteId == null) return;
    setIsLoadingDelete(true);
    try {
      const response = await deleteApi(`${API_ANNOUNCEMENT}/${selectedDeleteId}`, accessToken);
      if (response.status === 200) {
        const nextRows = rows.filter((row) => row.id !== selectedDeleteId);
        setRows(nextRows);
        setShowDelete(false);
        setSelectedDeleteId(null);
        showSuccess(response.data.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const handleDeleteAttachment = async () => {
    if (selectedAttachmentId == null) return;
    setIsLoadingDelete(true);
    try {
      const response = await deleteApi(`${API_ANNOUNCEMENT_ATTACHMENT}/${selectedAttachmentId}`, accessToken);
      if (response) {
        setIsFileChange(false);
        setShowDeleteAttachment(false);
        setSelectedAttachmentId(null);
        showSuccess(response.data.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const columns: ResponsiveColumn<AnnouncementRow>[] = [
    { id: "title", header: "Title", accessor: "title", sortable: true, truncate: true },
    { id: "description", header: "Description", accessor: "description", truncate: true },
    { id: "eventType", header: "Event Type", accessor: "eventType", sortable: true, truncate: true },
    { id: "venue", header: "Venue", accessor: "venue", sortable: true, truncate: true },
    { id: "mode", header: "Mode", accessor: "mode", sortable: true },
    { id: "presentedBy", header: "Presented By", accessor: "presentedBy", sortable: true, truncate: true },
    { id: "startDateTime", header: "Start Date Time", accessor: "startDateTime", sortable: true, nowrap: true, render: (row) => moment(row.startDateTime).format("LLL") },
    { id: "endDateTime", header: "End Date Time", accessor: "endDateTime", sortable: true, nowrap: true, render: (row) => moment(row.endDateTime).format("LLL") },
    { id: "status", header: "State", accessor: "status", sortable: true, nowrap: true, render: (row) => <Badge variant={announcementStatusVariant(row.status)}>{announcementStatusLabel(row.status)}</Badge> },
    { id: "selectedStatus", header: "Status", nowrap: true, render: () => <Badge variant={announcementStatusVariant(selectedStatus)}>{announcementStatusLabel(selectedStatus)}</Badge> },
    {
      id: "actions", header: "Action", nowrap: true,
      render: (row) => <div className={styles.rowActions}>
        <Button size="sm" variant="muted-outline" startIcon={<Pencil size={15} />} onClick={() => openEdit(row)} aria-label={`Edit ${row.title}`}>Edit</Button>
        <Button size="sm" variant="danger-ghost" startIcon={<Trash2 size={15} />} onClick={() => { setSelectedDeleteId(row.id); setShowDelete(true); }} aria-label={`Delete ${row.title}`}>Delete</Button>
      </div>,
    },
  ];

  const existingPdf = selectedAnnouncement && isFileChange ? pdfFileName(selectedAnnouncement.attachment) : null;

  return <PageShell>
    <div className={styles.page}>

      <Card className={styles.toolbar}>
        <div className={styles.search}>
          <TextField id="announcement-search" label="Search by Title" placeholder="Enter Title" startIcon={<Search size={17} />} value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} onKeyDown={preventLeadingSpace} />
        </div>
        <div className={styles.statusFilter}>
          <Select id="announcement-status-filter" label="Status" value={selectedStatus} options={STATUS_OPTIONS} onChange={(value) => { setSelectedStatus(value || "PENDING"); setSearchTerm(""); }} />
        </div>
        <div className={styles.toolbarAction}><Button startIcon={<Plus size={17} />} variant="success" onClick={openCreate}>Create Announcement</Button></div>
      </Card>
      {isLoading ? <div className={styles.loading}>Loading announcements...</div> :
        <ResponsiveDataTable<AnnouncementRow>
          aria-label="Manage announcements"
          columns={columns}
          data={filteredRows}
          serialNumber
          page={currentPage}
          pageSize={pageSize}
          totalItems={totalRequest}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          pageSizeOptions={[5, 10, 20, 30, 40]}
          emptyState={<div className={styles.empty}>No Record Available</div>}
        />
      }

    </div>

    <Modal open={showEditor} onClose={() => { setShowEditor(false); resetForm(); }} title={`${selectedAnnouncement ? "Edit" : "Create"} Announcement`} size="lg"
      footer={<div className={styles.modalFooter}>
        <Button variant="light-grey" disabled={isButtonLoading} onClick={() => { setShowEditor(false); resetForm(); }}>Cancel</Button>
        <Button type="submit" form="announcement-form" disabled={isButtonLoading}>{isButtonLoading ? "Saving..." : selectedAnnouncement ? "Update" : "Submit"}</Button>
      </div>}>
      <form id="announcement-form" className={styles.form} onSubmit={(event) => void handleSubmit(event)}>
        <div className={styles.formGrid}>
          <Select id="announcement-event-type" label="Event Type" required error={errors.eventType} placeholder="Select Event Type" value={formData.eventType} options={eventTypeOptions} onChange={(value) => updateField("eventType", value)} />
          <TextField id="announcement-title" label="Title" required error={errors.title} value={formData.title} maxLength={150} placeholder="Enter Title" onChange={(event) => updateField("title", event.target.value)} onKeyDown={preventLeadingSpace} />
          <div className={styles.dateField}>
            <label className={`sub-heading-md ${styles.dateLabel}`} htmlFor="announcement-start">
              Start Date Time<span className="text-danger text-nowrap" aria-hidden>{"\u00A0*"}</span>
            </label>
            <ReactDatePicker id="announcement-start" selected={formData.startDateTime} onChange={(date: Date | null) => updateField("startDateTime", date)} minDate={new Date()} showTimeSelect filterTime={filterPassedTime} dateFormat="MMMM d, yyyy h:mm aa" placeholderText="YYYY-MM-DDTHH:mm:ss" className={styles.dateInput} wrapperClassName={styles.datePicker} portalId={V2_DATEPICKER_PORTAL_ID} required />
            {errors.startDateTime ? <p className={styles.error}>{errors.startDateTime}</p> : null}
          </div>
          <div className={styles.dateField}>
            <label className={`sub-heading-md ${styles.dateLabel}`} htmlFor="announcement-end">
              End Date Time<span className="text-danger text-nowrap" aria-hidden>{"\u00A0*"}</span>
            </label>
            <ReactDatePicker id="announcement-end" selected={formData.endDateTime} onChange={(date: Date | null) => updateField("endDateTime", date)} minDate={new Date()} showTimeSelect filterTime={filterPassedTime} dateFormat="MMMM d, yyyy h:mm aa" placeholderText="YYYY-MM-DDTHH:mm:ss" className={styles.dateInput} wrapperClassName={styles.datePicker} portalId={V2_DATEPICKER_PORTAL_ID} required />
            {errors.endDateTime ? <p className={styles.error}>{errors.endDateTime}</p> : null}
          </div>
          {formData.eventType !== "Display Announcement" ? <>
            <TextField id="announcement-presented-by" label="Presented By" required error={errors.presentedBy} value={formData.presentedBy} maxLength={30} placeholder="Enter Presenter Name" onChange={(event) => updateField("presentedBy", event.target.value)} onKeyDown={(event) => {
              if (event.key.length === 1 && !/^[a-zA-Z, ]$/.test(event.key)) event.preventDefault();
              if ((event.key === " " || event.key === ",") && event.currentTarget.selectionStart === 0) event.preventDefault();
            }} />
            <TextField id="announcement-venue" label="Venue" required error={errors.venue} value={formData.venue} maxLength={160} placeholder="Enter Venue" onChange={(event) => updateField("venue", event.target.value)} />
            <TextField id="announcement-mode" label="Mode" required error={errors.mode} value={formData.mode} maxLength={160} placeholder="Enter Mode" onChange={(event) => updateField("mode", event.target.value)} />
            <div className={styles.dateField}>
              {existingPdf ? <>
                <span className={styles.fileLabel}>File</span>
                <div className={styles.existingFile}><span>{existingPdf}</span><Button size="sm" variant="danger-ghost" startIcon={<Trash2 size={15} />} onClick={() => { setSelectedAttachmentId(selectedAnnouncement!.id); setShowDeleteAttachment(true); }}>Remove</Button></div>
              </> : <FileField key={fileInputKey} id="announcement-file" label="File" accept=".pdf,.jpg,.jpeg,.png" stagedFile={formData.file instanceof File ? formData.file : null} hint="PDF, JPG, PNG, or JPEG only. Maximum 8 MB." onChange={handleFileChange} />}
            </div>
          </> : null}
          <div className={styles.fullWidth}>
            <TextArea id="announcement-description" label="Description" required error={errors.description} rows={3} maxLength={160} value={formData.description} placeholder="Enter Description" onChange={(event) => updateField("description", event.target.value)} onKeyDown={(event) => {
              preventLeadingSpace(event);
              if (event.key.length === 1 && !/^[a-zA-Z0-9 ]$/.test(event.key)) event.preventDefault();
            }} />
          </div>
          {selectedAnnouncement ? <div className={styles.fullWidth}><Select id="announcement-edit-status" label="Status" required value={formData.status} options={STATUS_OPTIONS} placeholder="Select Status" onChange={(value) => updateField("status", value)} /></div> : null}
        </div>
      </form>
    </Modal>

    <Modal open={showDelete} onClose={() => { setShowDelete(false); setSelectedDeleteId(null); }} title="Delete Announcement" size="sm"
      footer={<div className={styles.modalFooter}>
        <Button variant="light-grey" disabled={isLoadingDelete} onClick={() => { setShowDelete(false); setSelectedDeleteId(null); }}>No</Button>
        <Button variant="danger" disabled={isLoadingDelete} onClick={() => void handleDelete()}>{isLoadingDelete ? "Deleting..." : "Yes"}</Button>
      </div>}>
      <p className={styles.confirmText}>Are you sure you want to delete this announcement?</p>
    </Modal>

    <Modal open={showDeleteAttachment} onClose={() => { setShowDeleteAttachment(false); setSelectedAttachmentId(null); }} title="Delete Attachment" size="sm"
      footer={<div className={styles.modalFooter}>
        <Button variant="light-grey" disabled={isLoadingDelete} onClick={() => { setShowDeleteAttachment(false); setSelectedAttachmentId(null); }}>No</Button>
        <Button variant="danger" disabled={isLoadingDelete} onClick={() => void handleDeleteAttachment()}>{isLoadingDelete ? "Deleting..." : "Yes"}</Button>
      </div>}>
      <p className={styles.confirmText}>Are you sure you want to delete the attachment from this announcement?</p>
    </Modal>
  </PageShell>;
}
